import type { AccountView, DataFlag, SignedStep, SigningPayload } from "../api/types.js";
import { formatLimit, formatMoney, lastDigits, toMinor } from "../lib/format.js";
import { describeStep, shownAs } from "./describe.js";
import { byAccountRank } from "./home-model.js";

/**
 * DCTA-170 — what the card's edit state says about a step (docs/design/v3/03-edit-in-place.md §0.2; copy §F.2 row 22),
 * from the signed step alone. The app composes only its own lines (the controls' names, the sheet's who/was/available
 * lines, the zeros check); every figure is the payload's decimal string, formatted, and the one comparison (the zeros
 * check) is on the typed figure against the signed one, in integer minor units — no arithmetic reaches a payload.
 */

/** The step's own money: what its amount field changes (an order's is its cash amount). */
export function amountOf(step: SignedStep): { readonly value: string; readonly currency: string } | undefined {
  if (step.type === "securities_order") return step.cash_amount;
  if (step.type === "funds_transfer" || step.type === "bill_payment") return step.amount;
  return undefined;
}

/**
 * Row 11's {what}, as the bank names a step in its sentences: "the S$80.00 to Mum" (the payee as the row's title names
 * them), "the S$1,000.00 to your Joint Account ···2280", "the S$412.00 to Platinum Card", "the S$500.00 order for DBS
 * Group Holdings Ltd". A string the bank flagged is its inert form, as on the row (DCTA-143).
 */
export function whatOf(step: SignedStep, flags: readonly DataFlag[] = []): string {
  const m = amountOf(step);
  const amount = m === undefined ? "" : formatMoney(m.value, m.currency);
  const name = describeStep(step, flags).name;
  switch (step.type) {
    case "securities_order":
      return `the ${amount} order for ${name}`;
    case "funds_transfer":
      if (step.payee === undefined && step.to_account !== undefined) return `the ${amount} to your ${name} ${lastDigits(step.to_account.masked_number)}`;
      return `the ${amount} to ${name}`;
    case "bill_payment":
      return `the ${amount} to ${name}`;
    default:
      return name;
  }
}

/**
 * ADR-0022: the quiet line under a figure typed above a limit the customer set earlier in the chat — "More than the S$500
 * you set earlier" (the coordinator's example; final words: ux-designer's copy owner, journal DCTA-170). None without the
 * bank's field.
 */
export function boundLine(step: SignedStep): string | undefined {
  const b = step.amended?.bound;
  return b === undefined ? undefined : `More than the ${formatLimit(b.value, b.currency)} you set earlier`;
}

/** 22c, 22d, 22s: the names of a row's controls. */
export function controlNames(what: string): { readonly remove: string; readonly change: string; readonly undo: string } {
  return { remove: `Remove ${what}`, change: `Change ${what}`, undo: `Undo removing ${what}` };
}

/** The account a step's money comes from, as signed. */
function sourceOf(step: SignedStep): { readonly account_id: string; readonly display_name: string } | undefined {
  if (step.type === "securities_order") return step.settlement_account;
  if (step.type === "funds_transfer" || step.type === "bill_payment") return step.debit_account;
  return undefined;
}

/**
 * F39 (22o): where a payment's or a bill's money can come from — the customer's own deposit accounts in SGD, unlocked,
 * ranked as Home ranks them (mock-up 21-edit `source`); never a trading settlement account, never the account a move
 * goes to; nothing for an order (it settles where it settles) or the bank's funding move (it follows its order). A
 * list to pick from, not a rule: the bank checks the pick again (`set_source`) and refuses it in its own words.
 */
export function sourceChoices(step: SignedStep, accounts: readonly AccountView[]): AccountView[] {
  if ((step.type !== "funds_transfer" && step.type !== "bill_payment") || editKind(step) === "follows") return [];
  const to = step.type === "funds_transfer" ? step.to_account?.account_id : undefined;
  return byAccountRank(accounts).filter((a) => a.currency === "SGD" && !a.locked && !a.type.includes("investment") && a.account_id !== to);
}

/** "from {account}" is a control only where the money could come from somewhere else (§0.2: no underline that leads nowhere). */
export function offersSource(step: SignedStep, accounts: readonly AccountView[]): boolean {
  const from = sourceOf(step)?.account_id;
  return sourceChoices(step, accounts).some((a) => a.account_id !== from);
}

/** 22o: an account as the sheet names it — "Joint Account ···2280". */
export function accountLabel(a: { readonly display_name: string; readonly masked_number: string }): string {
  return `${a.display_name} ${lastDigits(a.masked_number)}`;
}

/**
 * The account control's name: the mock-up's "Change the account for {what}", with the words it shows ("from {account}")
 * at its end — WCAG 2.5.3, a name that holds its visible label (a deviation for ux-designer, README R114).
 */
export function sourceName(what: string, from: string): string {
  return `Change the account for ${what}, from ${from}`;
}

/**
 * 22g: "{Who} · from {account}" — the row's title without its verb ("Send to Mum" → "To Mum", "Pay Platinum Card" →
 * "Platinum Card", "Buy DBS Group Holdings Ltd" → "DBS Group Holdings Ltd").
 */
export function whoLine(step: SignedStep, flags: readonly DataFlag[] = []): string {
  const title = describeStep(step, flags).title;
  const who = /^(?:Send|Move) to /.test(title) ? title.replace(/^(?:Send|Move) to /, "To ") : title.replace(/^(?:Pay|Buy|Sell) /, "");
  const from = sourceOf(step);
  if (from === undefined) return who;
  const field = step.type === "securities_order" ? "settlement_account/display_name" : "debit_account/display_name";
  return `${who} · from ${shownAs(flags, step.step_id, field, from.display_name)}`;
}

/**
 * 22k: "{amount} available in {account}" — the bank's own projection's "before" for that account, signed with a /2 payload
 * (`bundle.projected`). A payload without it has no line: the app never works out what an account holds.
 */
export function availLine(step: SignedStep, payload: SigningPayload, flags: readonly DataFlag[] = []): string | undefined {
  const from = sourceOf(step);
  if (from === undefined) return undefined;
  const p = payload.bundle?.projected.find((x) => x.account_id === from.account_id);
  if (p === undefined) return undefined;
  const field = step.type === "securities_order" ? "settlement_account/display_name" : "debit_account/display_name";
  return `${formatMoney(p.available_before, p.currency)} available in ${shownAs(flags, step.step_id, field, from.display_name)}`;
}

/** 22i: what the figure was. */
export function wasLine(previous: string, currency = "SGD"): string {
  return `Was ${formatMoney(previous, currency)}`;
}

/**
 * 22j (P4, 50 and 50,000): ten times what it was, or more — "Check the zeros." "That's 10 times the S$80.00 it was."; when
 * the ratio is not whole, "That's more than {k} times…". Under ten times: nothing (the "Was" line stands).
 */
export function zerosCheck(previous: string, typed: string, currency = "SGD"): { readonly lead: string; readonly rest: string } | undefined {
  const was = toMinor(previous);
  const now = toMinor(typed);
  if (was <= 0n || now < was * 10n) return undefined;
  const k = now / was;
  const whole = now % was === 0n;
  return { lead: "Check the zeros.", rest: `That's ${whole ? "" : "more than "}${k.toString()} times the ${formatMoney(previous, currency)} it was.` };
}

/** The figure typed in the sheet as a payload amount ("800.50"): two decimals at most, above zero; else undefined (22n). */
/** The amount field's whole digits at most: what the words line can say ("…billion…") and a canonical money string can carry. */
export const MAX_WHOLE_DIGITS = 12;

export function typedValue(typed: string): string | undefined {
  const m = /^(\d{1,3}(?:,\d{3})+|\d*)(?:\.(\d{0,2}))?$/.exec(typed.trim());
  if (m === null) return undefined;
  const whole = (m[1] ?? "").replace(/,/g, "").replace(/^0+(?=\d)/, "") || "0";
  if (whole.length > MAX_WHOLE_DIGITS) return undefined;
  const cents = (m[2] ?? "").padEnd(2, "0");
  if (whole === "0" && /^0*$/.test(cents)) return undefined;
  if ((m[1] ?? "") === "" && (m[2] ?? "") === "") return undefined;
  return `${whole}.${cents}`;
}

/**
 * What a row offers in the edit state (§0.2): "amount" — the remove disc and the amount field; "follows" — the bank's
 * funding move (DCTA-160's `shortfall_for_steps`): it changes with its order and goes with it, so neither; "none" — a
 * step with no money to change (an add, a protective action): the disc only.
 */
export function editKind(step: SignedStep): "amount" | "follows" | "none" {
  if (step.amount_derivation?.kind === "shortfall_for_steps") return "follows";
  // The amount sheet is SGD-fixed (§2.3): a figure in another currency is not offered for change.
  return amountOf(step)?.currency === "SGD" ? "amount" : "none";
}

/** The steps that change with `stepId`: one that depends on it, or whose amount is a share of it. */
export function dependantsOf(steps: readonly SignedStep[], stepId: string): SignedStep[] {
  return steps.filter((s) => s.step_id !== stepId && ((s.depends_on ?? []).includes(stepId) || s.amount_derivation?.from_step === stepId));
}

/**
 * What an edit of this step changes besides it — the bank asks first (`preview`) whenever there is any: its dependants,
 * and (H3, DCTA-171) the bank's own funding move a buy depends on (`shortfall_for_steps`), which is sized to the buys it
 * pays for and so follows the buy's edit — resized, or gone with it.
 */
export function affectedBy(steps: readonly SignedStep[], stepId: string): SignedStep[] {
  const step = steps.find((s) => s.step_id === stepId);
  const funding = steps.filter((s) => s.step_id !== stepId && (step?.depends_on ?? []).includes(s.step_id) && s.amount_derivation?.kind === "shortfall_for_steps");
  return [...dependantsOf(steps, stepId), ...funding];
}
